import { useRef, useCallback, useState, type KeyboardEvent } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Bold, Italic, Heading2, List, ListOrdered, Minus } from "lucide-react";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import { MarkdownReact } from "@/components/MarkdownReact";
import { cn } from "@/lib/utils";

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

/**
 * Markdown editor with a formatting toolbar and write/preview tabs.
 * @param props - Editor props including value, onChange, placeholder, and className
 * @returns The markdown editor component
 */
export function MarkdownEditor({
  value,
  onChange,
  placeholder = "Write markdown...",
  className,
}: MarkdownEditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [activeTab, setActiveTab] = useState("write");

  /**
   * Toggles wrap formatting around the selection. If already wrapped, unwraps it.
   * @param prefix - Text to insert before the selection
   * @param suffix - Text to insert after the selection
   * @param defaultText - Placeholder text when nothing is selected
   */
  const toggleWrap = useCallback(
    (prefix: string, suffix: string, defaultText: string) => {
      const textarea = textareaRef.current;
      if (!textarea) {
        return;
      }

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const selected = value.substring(start, end);

      // Check if the selection itself is wrapped (e.g. user selected "**bold**")
      if (
        selected.startsWith(prefix) &&
        selected.endsWith(suffix) &&
        selected.length >= prefix.length + suffix.length
      ) {
        const unwrapped = selected.slice(prefix.length, -suffix.length);
        const newValue =
          value.substring(0, start) + unwrapped + value.substring(end);
        onChange(newValue);
        requestAnimationFrame(() => {
          textarea.focus();
          textarea.setSelectionRange(start, start + unwrapped.length);
        });
        return;
      }

      // Check if the text around the selection is already wrapped
      const outerStart = start - prefix.length;
      const outerEnd = end + suffix.length;
      if (
        outerStart >= 0 &&
        outerEnd <= value.length &&
        value.substring(outerStart, start) === prefix &&
        value.substring(end, outerEnd) === suffix
      ) {
        const newValue =
          value.substring(0, outerStart) + selected + value.substring(outerEnd);
        onChange(newValue);
        requestAnimationFrame(() => {
          textarea.focus();
          textarea.setSelectionRange(outerStart, outerStart + selected.length);
        });
        return;
      }

      // Not wrapped — apply formatting
      const text = selected || defaultText;
      const before = value.substring(0, start);
      const after = value.substring(end);
      const newValue = `${before}${prefix}${text}${suffix}${after}`;
      onChange(newValue);

      requestAnimationFrame(() => {
        textarea.focus();
        const cursorStart = start + prefix.length;
        const cursorEnd = cursorStart + text.length;
        textarea.setSelectionRange(cursorStart, cursorEnd);
      });
    },
    [value, onChange],
  );

  /**
   * Toggles a line prefix at the start of the current line. Removes it if already present.
   * @param prefix - The prefix to toggle (e.g. "- ", "1. ", "## ")
   */
  const toggleLinePrefix = useCallback(
    (prefix: string) => {
      const textarea = textareaRef.current;
      if (!textarea) {
        return;
      }

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const lineStart = value.lastIndexOf("\n", start - 1) + 1;
      const lineContent = value.substring(lineStart);

      // Already has the prefix — remove it
      if (lineContent.startsWith(prefix)) {
        const newValue =
          value.substring(0, lineStart) + lineContent.substring(prefix.length);
        onChange(newValue);
        requestAnimationFrame(() => {
          textarea.focus();
          const newStart = Math.max(lineStart, start - prefix.length);
          const newEnd = Math.max(lineStart, end - prefix.length);
          textarea.setSelectionRange(newStart, newEnd);
        });
        return;
      }

      // Not present — add it
      const newValue = value.substring(0, lineStart) + prefix + lineContent;
      onChange(newValue);

      requestAnimationFrame(() => {
        textarea.focus();
        const newStart = start + prefix.length;
        const newEnd = end + prefix.length;
        textarea.setSelectionRange(newStart, newEnd);
      });
    },
    [value, onChange],
  );

  /**
   * Inserts a horizontal rule at the cursor position.
   */
  const insertHorizontalRule = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) {
      return;
    }

    const start = textarea.selectionStart;
    const before = value.substring(0, start);
    const after = value.substring(start);
    const needsNewline = before.length > 0 && !before.endsWith("\n");
    const rule = `${needsNewline ? "\n" : ""}---\n`;

    const newValue = `${before}${rule}${after}`;
    onChange(newValue);

    requestAnimationFrame(() => {
      textarea.focus();
      const cursorPos = before.length + rule.length;
      textarea.setSelectionRange(cursorPos, cursorPos);
    });
  }, [value, onChange]);

  /**
   * Handles Enter on a list line: continues the list prefix, or ends the list if the line is empty.
   * @param e - The keyboard event
   * @returns true if the event was handled
   */
  const handleListEnter = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>): boolean => {
      const textarea = textareaRef.current;
      if (!textarea) {
        return false;
      }

      const cursor = textarea.selectionStart;
      const lineStart = value.lastIndexOf("\n", cursor - 1) + 1;
      const lineText = value.substring(lineStart, cursor);

      // Match list prefixes with optional leading whitespace
      const bulletMatch = lineText.match(/^(\s*)([-*+] )(.*)/);
      const orderedMatch = lineText.match(/^(\s*)(\d+)\. (.*)/);

      if (!bulletMatch && !orderedMatch) {
        return false;
      }

      const indent = bulletMatch ? bulletMatch[1] : orderedMatch![1];
      const content = bulletMatch ? bulletMatch[3] : orderedMatch![3];

      // Empty list item — remove the prefix and end the list
      if (!content.trim()) {
        e.preventDefault();
        const newValue =
          value.substring(0, lineStart) + value.substring(cursor);
        onChange(newValue);
        requestAnimationFrame(() => {
          textarea.focus();
          textarea.setSelectionRange(lineStart, lineStart);
        });
        return true;
      }

      // Continue the list on the next line
      e.preventDefault();
      const nextPrefix = bulletMatch
        ? `${indent}${bulletMatch[2]}`
        : `${indent}${Number(orderedMatch![2]) + 1}. `;
      const before = value.substring(0, cursor);
      const after = value.substring(cursor);
      const newValue = `${before}\n${nextPrefix}${after}`;
      onChange(newValue);

      requestAnimationFrame(() => {
        textarea.focus();
        const newCursor = cursor + 1 + nextPrefix.length;
        textarea.setSelectionRange(newCursor, newCursor);
      });
      return true;
    },
    [value, onChange],
  );

  /**
   * Handles Tab/Shift+Tab to indent or un-indent list items for nesting.
   * @param e - The keyboard event
   * @returns true if the event was handled
   */
  const handleListIndent = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>): boolean => {
      const textarea = textareaRef.current;
      if (!textarea) {
        return false;
      }

      const cursor = textarea.selectionStart;
      const lineStart = value.lastIndexOf("\n", cursor - 1) + 1;
      const lineText = value.substring(lineStart);
      const lineEnd = lineText.indexOf("\n");
      const fullLine =
        lineEnd === -1 ? lineText : lineText.substring(0, lineEnd);

      // Only handle Tab on list lines
      if (!/^\s*[-*+] |^\s*\d+\. /.test(fullLine)) {
        return false;
      }

      e.preventDefault();
      const offset = cursor - lineStart;

      if (e.shiftKey) {
        // Un-indent: remove up to 2 leading spaces
        const spacesToRemove = fullLine.startsWith("  ")
          ? 2
          : fullLine.startsWith(" ")
            ? 1
            : 0;
        if (spacesToRemove === 0) {
          return true;
        }
        const newLine = fullLine.substring(spacesToRemove);
        const newValue =
          value.substring(0, lineStart) +
          newLine +
          value.substring(lineStart + fullLine.length);
        onChange(newValue);
        requestAnimationFrame(() => {
          textarea.focus();
          const newCursor = lineStart + Math.max(0, offset - spacesToRemove);
          textarea.setSelectionRange(newCursor, newCursor);
        });
      } else {
        // Indent: add 2 spaces
        const newLine = `  ${fullLine}`;
        const newValue =
          value.substring(0, lineStart) +
          newLine +
          value.substring(lineStart + fullLine.length);
        onChange(newValue);
        requestAnimationFrame(() => {
          textarea.focus();
          const newCursor = lineStart + offset + 2;
          textarea.setSelectionRange(newCursor, newCursor);
        });
      }
      return true;
    },
    [value, onChange],
  );

  /**
   * Handles keyboard shortcuts for markdown formatting in the textarea.
   * @param e - The keyboard event
   */
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      // Handle Enter for list continuation
      if (e.key === "Enter" && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
        if (handleListEnter(e)) {
          return;
        }
      }

      // Handle Tab for list indentation
      if (e.key === "Tab") {
        if (handleListIndent(e)) {
          return;
        }
      }

      const mod = e.metaKey || e.ctrlKey;
      if (!mod) {
        return;
      }

      if (e.key === "b") {
        e.preventDefault();
        e.stopPropagation();
        toggleWrap("**", "**", "bold text");
      } else if (e.key === "i") {
        e.preventDefault();
        e.stopPropagation();
        toggleWrap("_", "_", "italic text");
      } else if (e.key === "e") {
        e.preventDefault();
        e.stopPropagation();
        toggleLinePrefix("## ");
      } else if (e.shiftKey && e.key === "8") {
        e.preventDefault();
        e.stopPropagation();
        toggleLinePrefix("- ");
      } else if (e.shiftKey && e.key === "7") {
        e.preventDefault();
        e.stopPropagation();
        toggleLinePrefix("1. ");
      }
    },
    [toggleWrap, toggleLinePrefix, handleListEnter, handleListIndent],
  );

  const toolbarButtons = [
    {
      icon: Bold,
      label: "Bold",
      shortcut: "⌘B",
      action: () => toggleWrap("**", "**", "bold text"),
    },
    {
      icon: Italic,
      label: "Italic",
      shortcut: "⌘I",
      action: () => toggleWrap("_", "_", "italic text"),
    },
    {
      icon: Heading2,
      label: "Heading",
      shortcut: "⌘E",
      action: () => toggleLinePrefix("## "),
    },
    {
      icon: List,
      label: "Bullet list",
      shortcut: "⌘⇧8",
      action: () => toggleLinePrefix("- "),
    },
    {
      icon: ListOrdered,
      label: "Numbered list",
      shortcut: "⌘⇧7",
      action: () => toggleLinePrefix("1. "),
    },
    {
      icon: Minus,
      label: "Horizontal rule",
      action: insertHorizontalRule,
    },
  ];

  return (
    <div className={cn("flex min-h-0 flex-col overflow-hidden", className)}>
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="flex min-h-0 flex-1 flex-col overflow-hidden"
      >
        <div className="mb-2 flex shrink-0 items-center justify-between border-b pb-2">
          <TabsList className="h-8">
            <TabsTrigger value="write" className="text-xs px-3 py-1">
              Write
            </TabsTrigger>
            <TabsTrigger value="preview" className="text-xs px-3 py-1">
              Preview
            </TabsTrigger>
          </TabsList>

          {activeTab === "write" && (
            <div className="flex items-center gap-0.5">
              {toolbarButtons.map((btn) => (
                <Tooltip key={btn.label} delayDuration={100}>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      tabIndex={-1}
                      onClick={btn.action}
                    >
                      <btn.icon className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p>
                      {btn.label}
                      {"shortcut" in btn && btn.shortcut && (
                        <span className="ml-1.5 text-muted-foreground">
                          {btn.shortcut}
                        </span>
                      )}
                    </p>
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>
          )}
        </div>

        <TabsContent
          value="write"
          className="mt-0 min-h-0 flex-1 flex-col overflow-hidden data-[state=active]:flex"
        >
          <Textarea
            ref={textareaRef}
            autosize={false}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="h-full min-h-0 flex-1 resize-none overflow-y-auto font-mono text-sm"
          />
        </TabsContent>

        <TabsContent
          value="preview"
          className="mt-0 min-h-0 flex-1 flex-col overflow-hidden data-[state=active]:flex"
        >
          <div className="h-full min-h-0 flex-1 overflow-y-auto rounded-md border border-input p-3">
            {value ? (
              <div className="prose prose-sm dark:prose-invert max-w-none">
                <MarkdownReact>{value}</MarkdownReact>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground italic">
                Nothing to preview
              </p>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
